import React, { useState } from 'react';
import { api } from '../utils/api';
import { AdminLayout } from '../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { AlertCircle, ExternalLink, ShieldCheck, TrendingUp, Server, Activity, CheckCircle, XCircle, Loader2 } from 'lucide-react';

const APP_VERSION = process.env.REACT_APP_VERSION || 'akali-frontend@0.2.0';
const APP_BUILD_DATE = process.env.REACT_APP_BUILD_DATE || new Date().toISOString().slice(0, 10);
const SENTRY_DASHBOARD = 'https://akali-fisioterapia-pilates.sentry.io/issues/';
const POSTHOG_DASHBOARD = 'https://eu.posthog.com/project/165459';

const HealthRow = ({ check }) => {
  const ok = check.status === 'ok';
  return (
    <li className="flex items-center justify-between py-2 border-b last:border-b-0" data-testid={`health-row-${check.name}`}>
      <div className="flex items-center gap-2">
        {ok ? <CheckCircle className="w-4 h-4 text-emerald-500" /> : <XCircle className="w-4 h-4 text-red-500" />}
        <span className="text-sm">{check.label}</span>
      </div>
      <Badge variant="outline" className={ok ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}>
        {ok ? 'OK' : (check.message || 'falhou')}
      </Badge>
    </li>
  );
};

export default function AdminSystem() {
  const [health, setHealth] = useState(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState(null);

  const runHealthCheck = async () => {
    setChecking(true); setError(null);
    try {
      const data = await api.admin.runHealthCheck();
      setHealth(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao executar verificação');
    } finally {
      setChecking(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold" data-testid="admin-system-title">Sistema</h1>
          <p className="text-gray-500">Observabilidade, versão e saúde da aplicação</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <a
            href={SENTRY_DASHBOARD}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="link-sentry-dashboard"
            className="flex flex-col gap-2 p-4 border rounded-lg hover:border-amber-500 transition-colors bg-card"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-purple-500" />
                <span className="font-medium">Sentry — Erros</span>
              </div>
              <ExternalLink className="w-4 h-4 text-gray-400" />
            </div>
            <p className="text-xs text-gray-500">akali-fisioterapia-pilates.sentry.io</p>
          </a>

          <a
            href={POSTHOG_DASHBOARD}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="link-posthog-dashboard"
            className="flex flex-col gap-2 p-4 border rounded-lg hover:border-amber-500 transition-colors bg-card"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-orange-500" />
                <span className="font-medium">PostHog — Analytics</span>
              </div>
              <ExternalLink className="w-4 h-4 text-gray-400" />
            </div>
            <p className="text-xs text-gray-500">eu.posthog.com/project/165459</p>
          </a>

          <div className="flex flex-col gap-2 p-4 border rounded-lg bg-card" data-testid="card-app-version">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-emerald-500" />
              <span className="font-medium">Versão</span>
            </div>
            <code className="text-xs bg-muted px-2 py-1 rounded font-mono w-fit">{APP_VERSION}</code>
            <p className="text-xs text-gray-500">Último deploy: {APP_BUILD_DATE}</p>
          </div>
        </div>

        <Card>
          <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-amber-500" /> Saúde do app
              </CardTitle>
              <CardDescription>Verifica conectividade com integrações críticas</CardDescription>
            </div>
            <Button onClick={runHealthCheck} disabled={checking} data-testid="run-health-check-btn">
              {checking ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verificando…</> : <><Server className="w-4 h-4 mr-2" /> Verificar saúde do app</>}
            </Button>
          </CardHeader>
          <CardContent>
            {error && (
              <div className="flex items-center gap-2 text-red-500 text-sm mb-3">
                <AlertCircle className="w-4 h-4" /> {error}
              </div>
            )}
            {health ? (
              <div data-testid="health-check-results">
                <div className="flex items-center justify-between mb-3 pb-3 border-b">
                  <p className="text-sm">
                    Resultado:{' '}
                    {health.overall === 'ok' ? (
                      <span className="text-emerald-600 font-semibold">Tudo operacional</span>
                    ) : (
                      <span className="text-amber-600 font-semibold">{health.failures || 0} falha(s)</span>
                    )}
                  </p>
                  <p className="text-xs text-gray-500">{new Date(health.checked_at).toLocaleString('pt-BR')}</p>
                </div>
                <ul>
                  {(health.checks || []).map((c) => <HealthRow key={c.name} check={c} />)}
                </ul>
              </div>
            ) : (
              <p className="text-sm text-gray-500 py-2">
                Clique em <strong>"Verificar saúde do app"</strong> para rodar uma checagem em MongoDB, Stripe, Groq Whisper e Gemini.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
